// Guarda do backoffice: sem sessão → login; sem papel de staff → "sem acesso";
// staff sem 2FA → ativar 2FA (obrigatório). Só depois mostra a app. O backend repete estas regras em cada pedido.
import { useAuthLoader, useMe, useRefreshMe } from "@ecliptica/api-client/react";
import { TwoFactorSetup } from "@ecliptica/game-ui";
import { useTranslation } from "@ecliptica/i18n";
import { Alert, Button, Card, CardContent, Skeleton } from "@ecliptica/ui";
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6">{children}</CardContent>
      </Card>
    </div>
  );
}

export function RequireStaff() {
  const { t } = useTranslation();
  const me = useMe();
  const loadAuth = useAuthLoader();
  const refreshMe = useRefreshMe();
  const location = useLocation();

  if (me.isPending) {
    return (
      <div className="p-6">
        <Skeleton className="h-10 w-64" />
      </div>
    );
  }
  if (!me.data) return <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname)}`} replace />;

  const signOut = async () => {
    await (await loadAuth()).signOut();
    await refreshMe();
  };

  if (!me.data.isStaff) {
    return (
      <Centered>
        <h1 className="text-xl font-extrabold text-gold">{t("guard.noAccessTitle")}</h1>
        <Alert variant="warning" className="mt-4">
          {t("guard.noAccess", { email: me.data.user.email })}
        </Alert>
        <Button className="mt-4 w-full" onClick={() => void signOut()}>
          {t("auth.signOut")}
        </Button>
      </Centered>
    );
  }
  if (!me.data.user.twoFactorEnabled) {
    return (
      <Centered>
        <Alert variant="warning" className="mb-5">
          <strong>{t("guard.twoFactorTitle")}.</strong> {t("guard.twoFactor")}
        </Alert>
        <TwoFactorSetup onDone={() => void refreshMe()} />
        <Button variant="ghost" className="mt-3 w-full" onClick={() => void signOut()}>
          {t("auth.signOut")}
        </Button>
      </Centered>
    );
  }
  return <Outlet />;
}

/** true se a pessoa ligada tem esta permissão (para esconder botões e secções). */
export function useCan() {
  const me = useMe();
  const perms = new Set(me.data?.permissions ?? []);
  return (p: string) => perms.has(p as never);
}
